import {
  GameState, GameAction, GameEvent, DispatchResult,
  PlayerSide, PlayerState, CardInstance, CardDef, DeckDef, RingSlot,
} from '../types.js';
import { getConfig } from '../config.js';
import type { GameRule } from '../shared/rules.js';
import { shuffle, buildDeck, drawCards, createInstanceId } from './DeckUtils.js';
import { createPRNG, clonePRNG, nextFloat, generateSeed } from './PRNG.js';
import { canPlayCharacter, canSupport, canActivateAbility, canPlayAction, canDeclareAttackers, canAssignBlocker } from './Rules.js';
import { gainSpirit, spendSpirit } from './SpiritManager.js';
import { processAbility, getPassivePowerModifiers, getPassiveSupportBonus, hasAnyPassiveInRing, hasPassive, fireOnAttackTriggers, fireOnAttackAloneTriggers, fireOnPlayCharacterTriggers, fireOnPlayActionTriggers, getSupportsPerTurn } from './AbilityProcessor.js';
import { sweepPassiveKOs } from './CombatResolver.js';
import { logCardLink } from '../shared/log-markers.js';
import { narrateAbilityEvents } from './LogNarration.js';

// Passives whose effect is most legible at the moment the character enters
// the ring — board-wide modifiers, lockdowns, and the support-overlap
// loosener. These banner via an `ability-triggered` event in PLAY_CHARACTER.
// Passives not in this set banner at the moment they actually do work (e.g.
// passive-support-bonus during SUPPORT_CHARACTER, passive-leech during
// damage-to-opponent, passive-cannot-block at counter-phase entry).
const ON_PLAY_BANNER_PASSIVES = new Set([
  'passive-boost-allies',
  'passive-intimidate',
  'passive-hand-size',
  'passive-disable-activated',
  'passive-disable-on-play',
  'passive-easy-support',
]);

export function createInitialState(
  playerDeck: DeckDef,
  opponentDeck: DeckDef,
  cardCatalog: Map<string, CardDef>,
  rule?: GameRule,
  seed?: number,
): GameState {
  const config = getConfig();

  // Per-state PRNG + instance counter. Both used to be module globals; they
  // moved onto the state so AI tree search can fork independent simulations
  // without colliding with the live game.
  const rng = createPRNG(seed ?? generateSeed());
  const counter = { nextInstanceId: 0 };

  const maxHp = rule?.maxHp ?? config.startingHp;
  const playerInitialHp = rule?.initialPlayerHp ?? maxHp;
  const opponentInitialHp = rule?.initialOpponentHp ?? maxHp;
  const startingHandSize = rule?.startingHandSize ?? config.startingHandSize;
  const startingSpirit = rule?.startingSpirit ?? 0;
  const startingClock = rule?.startingClock ?? 0;
  // Resolve who goes first. null/undefined → seeded coin flip; otherwise
  // honour the rule's deterministic choice. The coin-flip animation in the
  // UI plays in either case so the player sees the same ceremony.
  const firstPlayer: PlayerSide =
    rule?.startingPlayer ?? (nextFloat(rng) < 0.5 ? 'player' : 'opponent');

  const playerBuild = buildDeck(playerDeck, cardCatalog, 'player', counter);
  const opponentBuild = buildDeck(opponentDeck, cardCatalog, 'opponent', counter);

  if (playerBuild.errors.length > 0 || opponentBuild.errors.length > 0) {
    console.warn('Deck build warnings:', [...playerBuild.errors, ...opponentBuild.errors]);
  }

  const cardInstances = new Map<string, CardInstance>();
  for (const inst of [...playerBuild.instances, ...opponentBuild.instances]) {
    cardInstances.set(inst.instanceId, inst);
  }

  const shuffledPlayerDeck = shuffle(rng, playerBuild.instances.map(i => i.instanceId));
  const shuffledOpponentDeck = shuffle(rng, opponentBuild.instances.map(i => i.instanceId));

  // requirePlayable: applies to the player only — the AI doesn't need a
  // tutorial-style guarantee. If the natural top-of-deck has no playable
  // character (clock ≤ 1, spirit ≥ 0), swap a deeper playable into the hand
  // so a new player isn't stuck with nothing to do.
  const playerDrawn = drawHandRespectingRule(
    shuffledPlayerDeck, cardInstances, cardCatalog, startingHandSize, !!rule?.requirePlayable,
  );
  const opponentDrawn = drawCards(shuffledOpponentDeck, startingHandSize);

  for (const id of playerDrawn.drawn) {
    cardInstances.get(id)!.zone = 'hand';
  }
  for (const id of opponentDrawn.drawn) {
    cardInstances.get(id)!.zone = 'hand';
  }

  const makeRing = (): RingSlot[] =>
    Array.from({ length: config.ringSlots }, () => ({ instanceId: null }));

  const playerState: PlayerState = {
    hp: playerInitialHp,
    spirit: startingSpirit,
    deck: playerDrawn.remaining,
    hand: playerDrawn.drawn,
    ring: makeRing(),
    discardPile: [],
    attackLine: [],
    ralliesUsed: 0,
    supportsUsed: 0,
    bonusRallies: 0,
    bonusSupports: 0,
    lastActivateHp: playerInitialHp,
    lastActivateDeck: playerDrawn.remaining.length,
    lastTurnHpDelta: 0,
    lastTurnDeckDelta: 0,
    attackedDuringTurn: false,
  };

  const opponentState: PlayerState = {
    hp: opponentInitialHp,
    spirit: startingSpirit,
    deck: opponentDrawn.remaining,
    hand: opponentDrawn.drawn,
    ring: makeRing(),
    discardPile: [],
    attackLine: [],
    ralliesUsed: 0,
    supportsUsed: 0,
    bonusRallies: 0,
    bonusSupports: 0,
    lastActivateHp: opponentInitialHp,
    lastActivateDeck: opponentDrawn.remaining.length,
    lastTurnHpDelta: 0,
    lastTurnDeckDelta: 0,
    attackedDuringTurn: false,
  };

  const state: GameState = {
    turnNumber: 1,
    currentPlayer: firstPlayer,
    firstPlayer,
    phase: 'activate',
    isFirstTurn: true,
    firstPlayerDrawsOnFirstTurn: rule?.firstPlayerDrawsOnFirstTurn ?? true,
    clock: startingClock,
    maxClock: rule?.maxClock ?? 12,
    maxHp,
    player: playerState,
    opponent: opponentState,
    cardInstances,
    combatState: null,
    winner: null,
    winReason: null,
    log: ['Game started!'],
    rng,
    nextInstanceId: counter.nextInstanceId,
  };

  // startingRing: place pre-set characters in each side's ring before turn 1.
  // Cards are instantiated fresh (independent of the deck) so the deck
  // composition is unaffected. They start non-fresh and non-exhausted, ready
  // to act on turn 1. On-play abilities DO fire — e.g. greek-hoplite's gain-
  // armor-on-play needs to apply so the pre-placed army actually has armor.
  // Positive spirit gain IS granted (negative-spirit costs are not charged)
  // so a player looking at their starting board can reconcile the SP shown
  // on each card with their starting SP — and so a "scenario gives you a
  // strong ally" gift (Tomoe ally stage) actually delivers the ally's
  // listed SP bonus. Rally consumption stays skipped: these placements are
  // scenario setup, not a turn-1 play.
  if (rule?.startingRing) {
    applyStartingRing(state, rule.startingRing.player, 'player', cardCatalog);
    applyStartingRing(state, rule.startingRing.opponent, 'opponent', cardCatalog);
  }

  return state;
}

/** Like `drawCards`, but if `require` is true and the top-N draw contains no
 *  playable character, swap a deeper playable card into hand position 0. */
function drawHandRespectingRule(
  deck: string[],
  instances: Map<string, CardInstance>,
  catalog: Map<string, CardDef>,
  handSize: number,
  require: boolean,
): { drawn: string[]; remaining: string[] } {
  if (!require) return drawCards(deck, handSize);
  const isPlayable = (instId: string): boolean => {
    const inst = instances.get(instId);
    if (!inst) return false;
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'character') return false;
    return (def.clock ?? 1) <= 1 && def.spirit >= 0;
  };
  if (deck.slice(0, handSize).some(isPlayable)) return drawCards(deck, handSize);
  const deeperIdx = deck.findIndex((id, i) => i >= handSize && isPlayable(id));
  if (deeperIdx < 0) return drawCards(deck, handSize); // none in deck — fallback
  const swapped = [...deck];
  [swapped[0], swapped[deeperIdx]] = [swapped[deeperIdx], swapped[0]];
  return drawCards(swapped, handSize);
}

function applyStartingRing(
  state: GameState,
  slugs: (string | null)[],
  owner: PlayerSide,
  catalog: Map<string, CardDef>,
): void {
  const ps = getPlayerState(state, owner);
  for (let i = 0; i < slugs.length && i < ps.ring.length; i++) {
    const slug = slugs[i];
    if (!slug) continue;
    const def = catalog.get(slug);
    if (!def) {
      console.warn(`startingRing: unknown card '${slug}' (slot ${i}, ${owner})`);
      continue;
    }
    if (def.type !== 'character') {
      console.warn(`startingRing: '${slug}' is not a character (slot ${i}, ${owner})`);
      continue;
    }
    const inst: CardInstance = {
      instanceId: createInstanceId(state),
      cardId: def.id,
      owner,
      currentPower: def.power,
      armor: 0,
      isFresh: false,
      isExhausted: false,
      supports: [],
      zone: 'ring',
      skipNextRefresh: false,
      abilityDisabled: false,
    };
    state.cardInstances.set(inst.instanceId, inst);
    ps.ring[i] = { instanceId: inst.instanceId };

    // Grant the card's listed SP bonus (positive only — pre-placed
    // characters aren't "played" so a negative-spirit cost isn't charged).
    // Matches what a player sees on the card and what they'd get if they
    // had played the character themselves.
    if (def.spirit > 0) {
      ps.spirit = gainSpirit(ps.spirit, def.spirit);
    }

    if (def.ability?.type === 'on-play') {
      // Discard returned events — initial-state setup happens before any UI
      // listener is attached, so there's nothing to deliver them to. Engine
      // state mutations apply directly.
      processAbility(state, inst.instanceId, def.ability, catalog);
      sweepPassiveKOs(state, [], catalog);
    }
  }
}

export function getPlayerState(state: GameState, side: PlayerSide): PlayerState {
  return side === 'player' ? state.player : state.opponent;
}

export function getOpponentSide(side: PlayerSide): PlayerSide {
  return side === 'player' ? 'opponent' : 'player';
}

export function getCardDef(cardId: string, catalog: Map<string, CardDef>): CardDef {
  const def = catalog.get(cardId);
  if (!def) throw new Error(`Card not found: ${cardId}`);
  return def;
}

/** Returns the play-clock threshold for a card. Cards without a `clock` field
 *  (e.g. existing or AI-generated cards from before the mechanic) default to
 *  1, meaning always playable. */
export function getCardClock(def: CardDef): number {
  return def.clock ?? 1;
}

/** Apply a clock delta with wrap (positive) and clamp (negative).
 *  Positive deltas wrap the play-clock around `state.maxClock` (12 by
 *  default; rapid-cycle rules cap lower): at clock == maxClock, a +1
 *  brings it to 1, +3 from maxClock-1 lands on 2, etc. The wrap
 *  formula always lands in [1, maxClock], so once the clock has left
 *  its initial 0 state it never returns to 0.
 *  Negative deltas clamp: from the initial 0, the clock stays at 0;
 *  once it's 1 or higher, decreases floor at 1.
 *  Mutates `state.clock` in place. */
export function adjustClock(state: GameState, delta: number): void {
  if (delta > 0) {
    const max = state.maxClock;
    state.clock = 1 + ((state.clock - 1 + delta) % max);
  } else if (delta < 0) {
    const floor = state.clock === 0 ? 0 : 1;
    state.clock = Math.max(floor, state.clock + delta);
  }
}

export function getInstance(state: GameState, instanceId: string): CardInstance {
  const inst = state.cardInstances.get(instanceId);
  if (!inst) throw new Error(`Instance not found: ${instanceId}`);
  return inst;
}

export function getEffectivePower(state: GameState, instanceId: string, catalog: Map<string, CardDef>): number {
  const inst = getInstance(state, instanceId);
  return inst.currentPower + getPassivePowerModifiers(state, instanceId, catalog);
}

export function cloneState(state: GameState): GameState {
  const clonedInstances = new Map<string, CardInstance>();
  for (const [id, inst] of state.cardInstances) {
    clonedInstances.set(id, { ...inst, supports: [...inst.supports] });
  }

  return {
    ...state,
    player: clonePlayerState(state.player),
    opponent: clonePlayerState(state.opponent),
    cardInstances: clonedInstances,
    combatState: state.combatState ? {
      groups: state.combatState.groups.map(g => ({ attackerIds: [...g.attackerIds], united: g.united })),
      blockerAssignments: new Map(state.combatState.blockerAssignments),
    } : null,
    log: [...state.log],
    rng: clonePRNG(state.rng),
  };
}

function clonePlayerState(ps: PlayerState): PlayerState {
  return {
    ...ps,
    deck: [...ps.deck],
    hand: [...ps.hand],
    ring: ps.ring.map(s => ({ ...s })),
    discardPile: [...ps.discardPile],
    attackLine: [...ps.attackLine],
  };
}

export function dispatch(
  state: GameState,
  action: GameAction,
  catalog: Map<string, CardDef>,
): DispatchResult {
  const next = cloneState(state);
  const events: GameEvent[] = [];
  const current = next.currentPlayer;
  const ps = getPlayerState(next, current);
  const opSide = getOpponentSide(current);
  const ops = getPlayerState(next, opSide);

  // Source card for the action being processed — used as `responsibleCardId`
  // on the game-over event when this action damages a player to HP 0. Pulled
  // from the action's instanceId before the action mutates state, so even if
  // the instance is removed from play (e.g. an action card going to discard)
  // we still have its cardId at the emit site. Null for actions that don't
  // carry an instance (CONFIRM_BLOCKERS, SKIP_PHASE, ADVANCE_PHASE — the
  // first of those routes its damage through CombatResolver which emits
  // game-over itself with its own attacker-derived responsibleCardId).
  const sourceCardId: string | null = (() => {
    if ('instanceId' in action && typeof action.instanceId === 'string') {
      return next.cardInstances.get(action.instanceId)?.cardId ?? null;
    }
    return null;
  })();

  switch (action.type) {
    case 'PLAY_CHARACTER': {
      const inst = getInstance(next, action.instanceId);
      const def = getCardDef(inst.cardId, catalog);
      if (def.type !== 'character') break;

      // Remove from hand
      ps.hand = ps.hand.filter(id => id !== action.instanceId);
      // Place in ring
      ps.ring[action.slotIndex] = { instanceId: action.instanceId };
      inst.zone = 'ring';
      inst.isFresh = true;
      inst.currentPower = def.power;
      ps.ralliesUsed++;

      events.push({ type: 'card-played', instanceId: action.instanceId, slotIndex: action.slotIndex, player: current });

      // Log the play FIRST so subsequent effect narration reads in
      // resolution order ("player played X" → "X: Boosted …"). If we
      // pushed this at the tail like the log used to, the effect line
      // would appear before its own cast line.
      next.log.push(`${current} played ${logCardLink(def)} to slot ${action.slotIndex + 1}`);

      // Spirit gain/cost
      if (def.spirit > 0) {
        const oldSpirit = ps.spirit;
        ps.spirit = gainSpirit(ps.spirit, def.spirit);
        events.push({ type: 'spirit-changed', player: current, oldSpirit, newSpirit: ps.spirit });
      } else if (def.spirit < 0) {
        const oldSpirit = ps.spirit;
        ps.spirit = spendSpirit(ps.spirit, Math.abs(def.spirit));
        events.push({ type: 'spirit-changed', player: current, oldSpirit, newSpirit: ps.spirit });
      }

      // On-play ability — suppressed if any character on either ring has the
      // passive-disable-on-play lockdown. Resolved before the trigger
      // event is emitted so a no-op effect (no targets / nothing to
      // do) doesn't surface a misleading banner.
      if (def.ability?.type === 'on-play'
          && !hasAnyPassiveInRing(next, 'passive-disable-on-play', catalog)) {
        const abilityEvents = processAbility(next, action.instanceId, def.ability, catalog);
        if (abilityEvents.length > 0) {
          events.push({ type: 'ability-triggered', instanceId: action.instanceId, abilityType: 'on-play', effect: def.ability.effect });
          events.push(...abilityEvents);
          // Narrate what actually changed on the board, not the rule
          // template. Skip the log push if narration is empty (e.g.
          // effect fired but produced no reader-visible events).
          const narration = narrateAbilityEvents(abilityEvents, next, catalog);
          if (narration) next.log.push(`${logCardLink(def)}: ${narration}`);
        }
      }

      // Passives whose effect is best understood at the moment the character
      // hits the ring (board-wide modifiers + lockdowns + easy-support) get
      // a banner via ability-triggered. Other passives banner at the moment
      // they actually do work (support, attack, damage-to-opponent, etc.) —
      // see those code paths.
      if (def.ability?.type === 'passive' && ON_PLAY_BANNER_PASSIVES.has(def.ability.effect)) {
        events.push({ type: 'ability-triggered', instanceId: action.instanceId, abilityType: 'passive', effect: def.ability.effect });
      }

      // Sweep for KOs — a new passive (e.g. intimidate) may push opponents to 0
      sweepPassiveKOs(next, events, catalog);

      // After the played character's own on-play / passive triggers have
      // fully resolved (and the sweep above has removed anything they
      // KO'd), fire on-play-character (💫) on the rest of the ring.
      // Excludes the just-played character (it shouldn't react to its
      // own arrival). Any character KO'd by the on-play is already in
      // `discard` and gets skipped by fireTriggerForIds. Suppressed by
      // the same lockdown as on-play (⚡) — passive-disable-on-play
      // reads "On-play (⚡ and 💫) abilities do not trigger."
      if (!hasAnyPassiveInRing(next, 'passive-disable-on-play', catalog)) {
        events.push(...fireOnPlayCharacterTriggers(next, action.instanceId, catalog));
      }
      sweepPassiveKOs(next, events, catalog);

      break;
    }

    case 'SUPPORT_CHARACTER': {
      const handInst = getInstance(next, action.handInstanceId);
      const ringInst = getInstance(next, action.ringInstanceId);
      const handDef = getCardDef(handInst.cardId, catalog);
      const ringDef = getCardDef(ringInst.cardId, catalog);

      // Remove from hand, attach as support
      ps.hand = ps.hand.filter(id => id !== action.handInstanceId);
      handInst.zone = 'discard'; // supports go to discard conceptually but are tracked
      ringInst.supports.push(action.handInstanceId);
      const oldPower = ringInst.currentPower;
      const passiveBonus = getPassiveSupportBonus(next, current, catalog);
      const totalBonus = getConfig().supportPowerBonus + passiveBonus;
      ringInst.currentPower += totalBonus;
      ps.supportsUsed++;

      events.push({ type: 'support-attached', handInstanceId: action.handInstanceId, ringInstanceId: action.ringInstanceId, player: current });
      events.push({ type: 'power-changed', instanceId: action.ringInstanceId, oldPower, newPower: ringInst.currentPower, amount: totalBonus });

      // passive-support-bonus: emit one banner per ally with this passive,
      // since the bonus actually applied (passiveBonus > 0 means at least
      // one such ally is on board).
      if (passiveBonus > 0) {
        for (const slot of ps.ring) {
          if (!slot.instanceId) continue;
          if (hasPassive(next, slot.instanceId, 'passive-support-bonus', catalog)) {
            const sourceDef = getCardDef(getInstance(next, slot.instanceId).cardId, catalog);
            if (sourceDef.type === 'character' && sourceDef.ability) {
              events.push({ type: 'ability-triggered', instanceId: slot.instanceId, abilityType: 'passive', effect: sourceDef.ability.effect });
            }
          }
        }
      }

      // passive-extra-support: emit only when this support consumed the
      // base quota AND the extended quota still has room — i.e., the next
      // support will exist solely because of this passive.
      const baseSupports = getConfig().baseSupportsPerTurn;
      const extendedSupports = getSupportsPerTurn(next, current, catalog);
      if (ps.supportsUsed >= baseSupports && ps.supportsUsed < extendedSupports) {
        for (const slot of ps.ring) {
          if (!slot.instanceId) continue;
          if (hasPassive(next, slot.instanceId, 'passive-extra-support', catalog)) {
            events.push({ type: 'ability-triggered', instanceId: slot.instanceId, abilityType: 'passive', effect: 'passive-extra-support' });
          }
        }
      }

      next.log.push(`${current} supported ${logCardLink(ringDef)} with ${logCardLink(handDef)} (+${totalBonus} Power)`);
      break;
    }

    case 'ACTIVATE_ABILITY': {
      const inst = getInstance(next, action.instanceId);
      const def = getCardDef(inst.cardId, catalog);
      if (def.type !== 'character' || def.ability?.type !== 'activated') break;
      // Defensive: a silenced character shouldn't be activatable. The
      // UI's Activate button is gated by `canActivateAbility` which
      // already checks `abilityDisabled`, but the AI / replay paths
      // dispatch ACTIVATE_ABILITY without that gate — fall through
      // silently so the action becomes a no-op and the character
      // doesn't get exhausted for nothing.
      if (inst.abilityDisabled) break;

      inst.isExhausted = true;
      events.push({ type: 'character-exhausted', instanceId: action.instanceId });

      // Resolve the activation before emitting its trigger event so
      // a no-op activation (e.g. damage on an empty enemy board)
      // doesn't banner. The character still exhausts — that's the
      // canonical "you spent the activation" signal even when the
      // effect found nothing to do.
      const abilityEvents = processAbility(next, action.instanceId, def.ability, catalog);
      if (abilityEvents.length > 0) {
        events.push({ type: 'ability-triggered', instanceId: action.instanceId, abilityType: 'activated', effect: def.ability.effect });
        events.push(...abilityEvents);
      }
      sweepPassiveKOs(next, events, catalog);

      const activatedNarration = narrateAbilityEvents(abilityEvents, next, catalog);
      if (activatedNarration) {
        next.log.push(`${logCardLink(def)}: ${activatedNarration}`);
      } else {
        // No narratable effect (e.g. exhaust-only cost paid but the
        // effect found no valid targets) — still record that the
        // activation happened so the reader isn't confused about the
        // exhausted state.
        next.log.push(`${current} activated ${logCardLink(def)}'s ability`);
      }
      break;
    }

    case 'PLAY_ACTION': {
      const inst = getInstance(next, action.instanceId);
      const def = getCardDef(inst.cardId, catalog);
      if (def.type !== 'action') break;

      // Remove from hand
      ps.hand = ps.hand.filter(id => id !== action.instanceId);
      inst.zone = 'discard';
      ps.discardPile.push(action.instanceId);

      // Pay spirit cost
      if (def.spirit < 0) {
        const oldSpirit = ps.spirit;
        ps.spirit = spendSpirit(ps.spirit, Math.abs(def.spirit));
        events.push({ type: 'spirit-changed', player: current, oldSpirit, newSpirit: ps.spirit });
      }

      events.push({ type: 'action-played', instanceId: action.instanceId, player: current, effect: def.effect });

      next.log.push(`${current} played action: ${logCardLink(def)}`);

      // Process effect
      const abilityEvents = processAbility(next, action.instanceId, { type: 'on-play', effect: def.effect, intensity: def.intensity, target: def.target }, catalog);
      events.push(...abilityEvents);
      sweepPassiveKOs(next, events, catalog);
      const actionNarration = narrateAbilityEvents(abilityEvents, next, catalog);
      if (actionNarration) next.log.push(`${logCardLink(def)}: ${actionNarration}`);

      // After the action's effect fully resolves, fire on-play-action
      // (💫) on the action-player's ring. The order ("effect first,
      // then reactive triggers") matches PLAY_CHARACTER and keeps the
      // rules text ("when you play an action card") meaning "when one
      // finishes resolving", not "interrupting one mid-resolution".
      // Suppressed by passive-disable-on-play: the 💫 reaction family
      // shares the lockdown with ⚡. (The action card's OWN effect at
      // line 475 still resolves — action cards aren't characters with
      // an `ability.type='on-play'`; they're their own category.)
      if (!hasAnyPassiveInRing(next, 'passive-disable-on-play', catalog)) {
        events.push(...fireOnPlayActionTriggers(next, current, catalog));
      }
      sweepPassiveKOs(next, events, catalog);

      break;
    }

    case 'DECLARE_ATTACKERS': {
      const config = getConfig();
      // Flag for SearchAI's doom-stall detector to read on its next
      // turn ("did opponent attack last turn?"). Cleared at the
      // attacker's next activate.
      ps.attackedDuringTurn = true;
      // Move all attackers to attack line
      for (const group of action.groups) {
        for (const id of group.attackerIds) {
          const inst = getInstance(next, id);
          inst.zone = 'attackLine';
          ps.attackLine.push(id);
        }
      }

      // Pay united attack costs
      for (const group of action.groups) {
        if (group.united && group.attackerIds.length >= 2) {
          const cost = group.attackerIds.length === 2 ? config.unitedAttackCost2 : config.unitedAttackCost3;
          const oldSpirit = ps.spirit;
          ps.spirit = spendSpirit(ps.spirit, cost);
          events.push({ type: 'spirit-changed', player: current, oldSpirit, newSpirit: ps.spirit });
        }
      }

      next.combatState = {
        groups: action.groups.map(g => ({ attackerIds: [...g.attackerIds], united: g.united })),
        blockerAssignments: new Map(),
      };

      const describeAttacker = (id: string) => {
        const inst = getInstance(next, id);
        const def = getCardDef(inst.cardId, catalog);
        return `${logCardLink(def)} (${getEffectivePower(next, id, catalog)})`;
      };
      const groupLabels = action.groups.map(g => {
        const attackers = g.attackerIds.map(describeAttacker).join(' + ');
        return g.united && g.attackerIds.length >= 2 ? `[united: ${attackers}]` : attackers;
      });
      events.push({ type: 'attack-declared', groups: action.groups, player: current });
      next.log.push(`${current} declared attack with ${groupLabels.join(', ')}`);

      // Fire on-attack triggers now, *before* the defender assigns blockers,
      // so an attacker's on-attack effect can remove a would-be blocker
      // from the ring instead of merely dueling it. koCharacter prunes
      // combat.groups, so a self-destructing trigger cleans itself up.
      const allAttackerIds = action.groups.flatMap(g => [...g.attackerIds]);
      // passive-attack-ready: fires alongside (logically before) on-attack
      // triggers so the banner stack reads in attack-time order. NOT
      // suppressed by passive-disable-activated — attack-ready is a
      // static "can attack this turn" modifier (no ⚔️/↩️ icon) rather
      // than a triggered combat ability.
      for (const id of allAttackerIds) {
        if (hasPassive(next, id, 'passive-attack-ready', catalog)) {
          events.push({ type: 'ability-triggered', instanceId: id, abilityType: 'passive', effect: 'passive-attack-ready' });
        }
      }
      // Combat triggers (⚔️) are suppressed by the same lockdown as
      // activated abilities — passive-disable-activated reads
      // "Characters can't use activated (↩️) or combat (⚔️) abilities."
      // on-damage-opponent fires later from CombatResolver and is gated
      // there too.
      if (!hasAnyPassiveInRing(next, 'passive-disable-activated', catalog)) {
        events.push(...fireOnAttackTriggers(next, allAttackerIds, catalog));
        if (action.groups.length === 1 && action.groups[0].attackerIds.length === 1) {
          events.push(...fireOnAttackAloneTriggers(next, action.groups[0].attackerIds[0], catalog));
        }
      }
      sweepPassiveKOs(next, events, catalog);
      break;
    }

    case 'ASSIGN_BLOCKER': {
      if (!next.combatState) break;
      // Remove this blocker from any previous assignment
      for (const [gIdx, bId] of next.combatState.blockerAssignments) {
        if (bId === action.blockerInstanceId && gIdx !== action.groupIndex) {
          next.combatState.blockerAssignments.delete(gIdx);
          break;
        }
      }
      next.combatState.blockerAssignments.set(action.groupIndex, action.blockerInstanceId);
      events.push({ type: 'blocker-assigned', groupIndex: action.groupIndex, blockerInstanceId: action.blockerInstanceId });

      const blockerInst = getInstance(next, action.blockerInstanceId);
      const blockerDef = getCardDef(blockerInst.cardId, catalog);
      const blockerPower = getEffectivePower(next, action.blockerInstanceId, catalog);
      const attackerGroup = next.combatState.groups[action.groupIndex];
      const attackerLinks = attackerGroup.attackerIds
        .map(id => logCardLink(getCardDef(getInstance(next, id).cardId, catalog)));
      const attackerLabel = attackerGroup.united && attackerLinks.length >= 2
        ? `[united: ${attackerLinks.join(' + ')}]`
        : attackerLinks.join(' + ');
      next.log.push(`${opSide} assigned ${logCardLink(blockerDef)} (${blockerPower}) to block ${attackerLabel}`);
      break;
    }

    case 'UNASSIGN_BLOCKER': {
      if (!next.combatState) break;
      for (const [gIdx, bId] of next.combatState.blockerAssignments) {
        if (bId === action.blockerInstanceId) {
          next.combatState.blockerAssignments.delete(gIdx);
          const blockerDef = getCardDef(getInstance(next, action.blockerInstanceId).cardId, catalog);
          next.log.push(`${opSide} unassigned ${logCardLink(blockerDef)} as blocker`);
          break;
        }
      }
      break;
    }

    case 'PLAY_REACTION': {
      const inst = getInstance(next, action.instanceId);
      const def = getCardDef(inst.cardId, catalog);
      if (def.type !== 'action' || def.subtype !== 'reaction') break;

      // Remove from hand of the opponent (who is the counter-attacker)
      ops.hand = ops.hand.filter(id => id !== action.instanceId);
      inst.zone = 'discard';
      ops.discardPile.push(action.instanceId);

      if (def.spirit < 0) {
        const oldSpirit = ops.spirit;
        ops.spirit = spendSpirit(ops.spirit, Math.abs(def.spirit));
        events.push({ type: 'spirit-changed', player: opSide, oldSpirit, newSpirit: ops.spirit });
      }

      events.push({ type: 'action-played', instanceId: action.instanceId, player: opSide, effect: def.effect });
      next.log.push(`${opSide} played reaction: ${logCardLink(def)}`);
      const abilityEvents = processAbility(next, action.instanceId, { type: 'on-play', effect: def.effect, intensity: def.intensity, target: def.target }, catalog);
      events.push(...abilityEvents);
      sweepPassiveKOs(next, events, catalog);
      const reactionNarration = narrateAbilityEvents(abilityEvents, next, catalog);
      if (reactionNarration) next.log.push(`${logCardLink(def)}: ${reactionNarration}`);

      // The reaction was played by `opSide` (the non-current player
      // counter-attacking), so fire on-play-action (💫) on their ring,
      // not the current player's. Same effect-first ordering and
      // same passive-disable-on-play lockdown as PLAY_ACTION.
      if (!hasAnyPassiveInRing(next, 'passive-disable-on-play', catalog)) {
        events.push(...fireOnPlayActionTriggers(next, opSide, catalog));
      }
      sweepPassiveKOs(next, events, catalog);

      break;
    }

    case 'DISCARD_CARD': {
      const inst = getInstance(next, action.instanceId);
      const side = inst.owner;
      const playerSt = getPlayerState(next, side);
      if (!playerSt.hand.includes(action.instanceId)) break;

      playerSt.hand = playerSt.hand.filter(id => id !== action.instanceId);
      inst.zone = 'discard';
      playerSt.discardPile.push(action.instanceId);

      const def = getCardDef(inst.cardId, catalog);
      events.push({ type: 'card-discarded', instanceId: action.instanceId, player: side });
      next.log.push(`${side} discarded ${logCardLink(def)}`);
      break;
    }

    case 'CONFIRM_BLOCKERS':
    case 'SKIP_PHASE':
    case 'ADVANCE_PHASE':
      // These are handled by TurnManager
      break;
  }

  // Check win conditions. Some effect handlers (currently
  // `damage-opponent` in AbilityProcessor) emit game-over inline so
  // the responsibleCardId points at the ability's owner rather than
  // the top-level action — which for on-attack triggers would be
  // DECLARE_ATTACKERS (no instanceId, null attribution). When they
  // do, they set `next.winner`; gate this fallback on that so we
  // don't double-emit.
  if (next.winner === null) {
    if (next.player.hp <= 0) {
      next.winner = 'opponent';
      next.winReason = 'hp';
      events.push({ type: 'game-over', winner: 'opponent', reason: 'hp', responsibleCardId: sourceCardId });
    } else if (next.opponent.hp <= 0) {
      next.winner = 'player';
      next.winReason = 'hp';
      events.push({ type: 'game-over', winner: 'player', reason: 'hp', responsibleCardId: sourceCardId });
    }
  }

  return { state: next, events };
}
